import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { FrontendApi, Configuration } from '@ory/client-fetch'
import type { TeamBasic } from '@/types/team'
import type { OrganizationPlanSummary } from '@/types/organization'

export interface OrganizationHeader {
  id: string
  name: string
}

export interface User {
  id: string
  username: string
  firstName?: string
  email: string

  // Platform admin role
  role: 'admin' | 'user'

  // Organizations where user is admin
  adminOrganizations?: OrganizationHeader[]

  // Teams where user is admin or delegate
  adminTeams?: TeamBasic[]

  // Current organization plan summary
  organizationPlan?: OrganizationPlanSummary

  // Eligibility resolved by the backend from organization membership or subscription.
  managedAiAccess?: boolean

  // Platform access status ('granted' or restriction reason)
  platformAccess?: string
}

export interface AuthState {
  user: User | null
  loading: boolean
  error: string | null
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Initialize Ory client
  const basePath = (window as any).APP_CONFIG?.ORY_URL || "http://localhost:4000"

  const ory = new FrontendApi(
    new Configuration({
      basePath,
      credentials: "include",
    }),
  )

  // Getters
  const isAuthenticated = computed(() => !!user.value)
  const userRole = computed(() => user.value?.role || 'user')
  const isPlatformAdmin = computed(() => user.value?.role === 'admin')
  const isOrgAdmin = computed(() => !!(user.value?.adminOrganizations && user.value.adminOrganizations.length > 0))
  const adminOrganizations = computed(() => user.value?.adminOrganizations || [])

  const isTeamAdmin = computed(() => (orgId?: string) => {
    if (!user.value?.adminTeams || user.value.adminTeams.length === 0) {
      return false
    }

    if (!orgId) {
      return true  // User is team admin/editor in ANY org
    }

    // Check if user is team admin/editor in specific org
    return user.value.adminTeams.some(team => team.organizationId === orgId)
  })

  // Actions
  const setUser = (userData: User | null) => {
    user.value = userData
    error.value = null
  }

  const setLoading = (state: boolean) => {
    loading.value = state
  }

  const setError = (errorMessage: string | null) => {
    error.value = errorMessage
  }

  const clearError = () => {
    error.value = null
  }

  // Check authentication status
  const checkAuth = async (): Promise<boolean> => {
    try {
      setLoading(true)
      clearError()

      // Dev-only test-auth: with VITE_TEST_AUTH=true the Vite dev proxy injects
      // the backend's INTEGRATION_TEST bypass headers into every /api request,
      // so the session comes from the backend, not Ory (useful when the local
      // Ory tunnel is unavailable). Dead code in production builds (DEV=false).
      if (import.meta.env.DEV && import.meta.env.VITE_TEST_AUTH === 'true') {
        const response = await fetch('/api/v1/users/me', { credentials: 'include' })
        if (!response.ok) {
          setUser(null)
          return false
        }
        const me = await response.json()
        setUser({
          id: me.id,
          username: me.username || me.email || '',
          firstName: me.firstName || '',
          email: me.email || '',
          role: me.role || 'user',
        })
        return true
      }

      // In test environment, skip auth check
      if (typeof window === 'undefined' || process.env.NODE_ENV === 'test') {
        setUser(null)
        return false
      }

      // Fetch the session directly from Ory
      const sessionData = await ory.toSession()
      
      if (sessionData && sessionData.identity) {
        // Map Ory session data to our User interface
        const userData: User = {
          id: sessionData.identity.id,
          username: sessionData.identity.traits?.username || sessionData.identity.traits?.email || '',
          firstName: sessionData.identity.traits?.name?.first || '',
          email: sessionData.identity.traits?.email || '',
          role: sessionData.identity.traits?.role || 'user'
        }
        setUser(userData)
        return true
      } else {
        setUser(null)
        return false
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication check failed')
      setUser(null)
      return false
    } finally {
      setLoading(false)
    }
  }

  // Login
  const login = async (loginIdentifier: string, password: string): Promise<User> => {
    try {
      setLoading(true)
      clearError()

      const response = await fetch('/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ loginIdentifier, password })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Login failed')
      }

      setUser(data.user)
      return data.user
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  // Signup
  const signup = async (username: string, email: string, password: string, role?: string): Promise<User> => {
    try {
      setLoading(true)
      clearError()

      const response = await fetch('/auth/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ username, email, password, role })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Signup failed')
      }

      setUser(data.user)
      return data.user
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Signup failed'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  // Logout
  const logout = async (): Promise<void> => {
    try {
      setLoading(true)
      clearError()

      const response = await fetch('/auth/logout', {
        method: 'POST',
        credentials: 'include'
      })

      if (!response.ok) {
        throw new Error('Logout failed')
      }

      setUser(null)

      // Reset auth initialization in router so it re-checks on next navigation
      const { resetAuthInitialization } = await import('@/router')
      resetAuthInitialization()
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Logout failed'
      setError(errorMessage)
      // Still clear user data even if logout request fails
      setUser(null)

      // Reset auth initialization even on error
      const { resetAuthInitialization } = await import('@/router')
      resetAuthInitialization()
      throw err
    } finally {
      setLoading(false)
    }
  }

  // Fetch current user from backend
  const fetchCurrentUser = async (): Promise<void> => {
    try {
      const response = await fetch('/api/v1/users/me', {
        credentials: 'include'
      })

      if (!response.ok) {
        // If unauthorized, clear user
        if (response.status === 401) {
          setUser(null)
        }
        return
      }

      const userData = await response.json()

      // Update user with backend data
      if (user.value) {
        user.value = {
          ...user.value,
          role: userData.role || 'user',
          adminOrganizations: userData.adminOrganizations || [],
          adminTeams: userData.adminTeams || [],
          organizationPlan: userData.organizationPlan || undefined,
          managedAiAccess: userData.subscription?.type === 'managed' || Object.values(userData.organization?.vault || {}).some(available => available === true),
          platformAccess: userData.platformAccess || undefined,
        }
      }
    } catch (err) {
      console.error('Failed to fetch current user:', err)
      // Don't throw - just log the error
    }
  }

  // Initialize auth state (call this on app startup)
  const initializeAuth = async (): Promise<void> => {
    const isAuthenticated = await checkAuth()
    if (isAuthenticated) {
      await fetchCurrentUser()
    }
  }

  return {
    // State
    user,
    loading,
    error,

    // Getters
    isAuthenticated,
    userRole,
    isPlatformAdmin,
    isOrgAdmin,
    adminOrganizations,
    isTeamAdmin,

    // Actions
    setUser,
    setLoading,
    setError,
    clearError,
    checkAuth,
    login,
    signup,
    logout,
    fetchCurrentUser,
    initializeAuth
  }
})